// The report card tab on Insights (and the free report card page): one week's, month's or quarter's
// grades, the uncomfortable truth and the assignments. The grading is in weeklyGrades.js.
import { useMemo, useState } from 'react';
import { formatMoney, tradingDayKey } from './pnl';
import { currentLocale } from './langState';
import { useI18n } from './i18n';
import { MIN_MONTH_TRADES, MIN_QUARTER_TRADES, MIN_WEEK_TRADES, addDays, asQuarter, minTradesFor, periodOf, reportCard, tradedPeriods } from './weeklyGrades';
import ImprovePanel from './ImprovePanel';

const gradeClass = (g) => `grade-${g[0].toLowerCase()}`;

// "Oct 12 – Oct 16": Monday to Friday of the week that starts on `weekKey` (a Sunday).
function weekLabel(weekKey) {
  const fmt = (key) => new Date(`${key}T12:00:00`).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' });
  return `${fmt(addDays(weekKey, 1))} – ${fmt(addDays(weekKey, 5))}`;
}

// "October 2026" for the month that starts on `monthKey`.
const monthLabel = (monthKey) => new Date(`${monthKey}T12:00:00`).toLocaleDateString(currentLocale(), { month: 'long', year: 'numeric' });

// "October to December 2026" for the quarter that starts on `quarterKey`.
function quarterLabel(quarterKey) {
  const [y, m] = quarterKey.split('-').map(Number);
  const name = (month) => new Date(Date.UTC(y, month - 1, 15)).toLocaleDateString(currentLocale(), { month: 'long', timeZone: 'UTC' });
  return `${name(m)} – ${name(m + 2)} ${y}`;
}

// Week, month or quarter wording: a quarter uses the month's sentence in quarter words.
function useWording(period) {
  const { t } = useI18n();
  return (weekText, monthText, vars) => (period === 'week' ? t(weekText, vars) : period === 'month' ? t(monthText, vars) : asQuarter(t(monthText, vars)));
}

// The grade against last week's: "▲ D → C−", or "Same grade as last week" (an arrow only
// when the letter actually changed).
function Change({ grade, previous, period }) {
  const w = useWording(period);
  if (!previous) return null;
  if (previous === grade) return <span className="report-change flat">{w('Same grade as last week', 'Same grade as last month')}</span>;
  const order = ['F', 'D-', 'D', 'D+', 'C-', 'C', 'C+', 'B-', 'B', 'B+', 'A-', 'A', 'A+'];
  const up = order.indexOf(grade) > order.indexOf(previous);
  return (
    <span className={`report-change ${up ? 'up' : 'down'}`} title={w('Last week', 'Last month')}>
      {up ? '▲' : '▼'} {previous} &rarr; {grade}
    </span>
  );
}

// `subjects` limits the grades (the free public version has no journal); `adminPreview` shows the badge.
// `onEarlier`: when there are older weeks the viewer can't see (the free page), the earlier-week
// button offers them instead of being disabled.
// `extraSubjects`: more cards after the graded ones (the free page shows its locked grades there).
// `renderFooter(card, periodText)`: extra content under the grades (the free page's share button).
// `onMonthLocked`: on the free page the month view needs an account, so the Month button calls this.
// Each grade opens "How to improve"; `onAction` saves its rules and experiments, and `improveLinks`
// ('all' or 'tools') says which of its links make sense here.
export default function ReportCard({ trades, notes, rules, library, accountName, subjects, noJournal = false, adminPreview = true, onEarlier, extraSubjects = null, renderFooter, onMonthLocked, onAction, improveLinks = 'all' }) {
  const { t, lang } = useI18n();
  const [openSubject, setOpenSubject] = useState(null);
  const [period, setPeriod] = useState('week');
  const month = period !== 'week';
  const w = useWording(period);
  const minTrades = minTradesFor(period);
  const periods = useMemo(() => tradedPeriods(trades, period), [trades, period]);
  const current = periodOf(tradingDayKey(new Date()), period);
  // Open on the latest week (or month) with enough trades for a grade (a file often ends mid-week).
  const firstKey = (p) => {
    const list = tradedPeriods(trades, p);
    const min = minTradesFor(p);
    const count = (k) => trades.filter((x) => periodOf(tradingDayKey(x.date), p) === k).length;
    return list.find((k) => count(k) >= min) || list[0] || periodOf(tradingDayKey(new Date()), p);
  };
  const [keys, setKeys] = useState(() => ({ week: firstKey('week'), month: firstKey('month'), quarter: firstKey('quarter') }));
  const week = keys[period];
  const setWeek = (k) => setKeys((all) => ({ ...all, [period]: k }));
  const card = useMemo(() => reportCard(trades, week, { notes, rules, library, subjects, noJournal, period }),
    [trades, week, notes, rules, library, subjects, noJournal, period, lang]); // eslint-disable-line react-hooks/exhaustive-deps
  const older = periods.find((w) => w < week);
  const newer = [...periods].reverse().find((w) => w > week);
  const periodText = period === 'quarter' ? quarterLabel(week) : month ? monthLabel(week) : t('Week of {dates}', { dates: weekLabel(week) });

  return (
    <section className="report-card" aria-labelledby="report-title">
      <div className="report-head">
        <div>
          <p className="eyebrow">{w('Weekly report card', 'Monthly report card')} {adminPreview && <span className="admin-preview-badge">{t('Admin only')}</span>}</p>
          <h2 id="report-title">{periodText}</h2>
          {(accountName || week === current) && (
            <p className="subtitle">{accountName}{accountName && week === current ? ' · ' : ''}{week === current ? w('week in progress', 'month in progress') : ''}</p>
          )}
        </div>
        <div className="report-controls">
          <div className="report-period" role="group" aria-label={t('Grade by')}>
            <button type="button" className={`secondary small${!month ? ' active' : ''}`} onClick={() => setPeriod('week')}>{t('Week')}</button>
            {[['month', 'Month'], ['quarter', 'Quarter']].map(([id, label]) => (
              <button key={id} type="button" className={`secondary small${period === id ? ' active' : ''}${onMonthLocked ? ' report-locked' : ''}`}
                onClick={() => (onMonthLocked ? onMonthLocked() : setPeriod(id))}>
                {onMonthLocked ? `🔒 ${t(label)}` : t(label)}
              </button>
            ))}
          </div>
          <div className="report-nav">
            {!older && onEarlier ? (
              <button type="button" className="secondary small report-locked" onClick={onEarlier}>&larr; {t('Earlier weeks: free with an account')}</button>
            ) : (
              <button type="button" className="secondary small" disabled={!older} onClick={() => setWeek(older)}>&larr; {w('Earlier week', 'Earlier month')}</button>
            )}
            <button type="button" className="secondary small" disabled={!newer} onClick={() => setWeek(newer)}>{w('Later week', 'Later month')} &rarr;</button>
          </div>
        </div>
      </div>

      {card.tooFew ? (
        <p className="empty">{w('Only {n} trades this week. A fair grade needs at least {min}.', 'Only {n} trades this month. A fair grade needs at least {min}.', { n: card.count, min: minTrades })}</p>
      ) : (
        <>
          <div className="report-top">
            <div className={`report-overall ${gradeClass(card.grade)}`}>
              <span className="report-overall-grade">{card.grade}</span>
              <span className="report-overall-label">{t('Overall')}</span>
              <Change grade={card.grade} previous={card.previousGrade} period={period} />
            </div>
            <div className="report-summary">
              <p className="report-summary-line">
                {t('{n} trades', { n: card.count })} &middot; <span className={card.net >= 0 ? 'gain' : 'loss'}>{formatMoney(card.net)}</span>
                {card.sloppyWin && <span className="report-sloppy">{t('Profitable, but sloppy')}</span>}
              </p>
              {card.fixFirst ? (
                <div className="report-truth">
                  <span className="report-truth-label">{t('The uncomfortable truth')} &middot; {card.fixFirst.label}</span>
                  <p>{card.fixFirst.comment}</p>
                  <p className="report-fix"><strong>{t('Fix this first:')}</strong> {card.fixFirst.tip}</p>
                  {card.fixFirst.improve && (
                    <button type="button" className="link-button report-fix-how" onClick={() => setOpenSubject(card.fixFirst.id)}>{t('See how to fix it')} &rarr;</button>
                  )}
                </div>
              ) : (
                <div className="report-truth good">
                  <span className="report-truth-label">{t('The honest truth')}</span>
                  <p>{w('No subject below a B this week. That’s a professional week. Do it again.', 'No subject below a B this month. That’s a professional month. Do it again.')}</p>
                </div>
              )}
            </div>
          </div>

          <p className="report-about-you">{w('Blunt on purpose. A low grade is about the week, not about you.', 'Blunt on purpose. A low grade is about the month, not about you.')}</p>

          <ul className="report-subjects">
            {card.subjects.map((s) => (
              <li key={s.id} className={`report-subject${card.fixFirst && card.fixFirst.id === s.id ? ' is-fix' : ''}${s.improve ? ' is-clickable' : ''}${openSubject === s.id ? ' is-open' : ''}`}
                {...(s.improve ? {
                  role: 'button', tabIndex: 0, 'aria-expanded': openSubject === s.id,
                  onClick: () => setOpenSubject(openSubject === s.id ? null : s.id),
                  onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenSubject(openSubject === s.id ? null : s.id); } },
                } : {})}>
                <div className="report-subject-head">
                  <span className={`report-grade ${gradeClass(s.grade)}`}>{s.grade}</span>
                  <div>
                    <strong>{s.label}</strong>
                    <Change grade={s.grade} previous={s.previousGrade} period={period} />
                  </div>
                </div>
                <p className="report-facts">{s.facts}</p>
                {card.fixFirst && card.fixFirst.id === s.id
                  ? <p className="report-comment report-see-fix">{w('This is this week’s uncomfortable truth, above.', 'This is this month’s uncomfortable truth, above.')}</p>
                  : <p className="report-comment">{s.comment}</p>}
                {s.sample.small && <p className="report-early">{t('Early signal: based on only {sample}.', { sample: s.sample.text })}</p>}
                {s.improve && openSubject === s.id && (
                  // Clicks inside the plan (its buttons and links) don't fold the card.
                  // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                  <div className="report-subject-improve" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                    <ImprovePanel embedded title={s.label} plan={s.improve} onAction={onAction} links={improveLinks} />
                  </div>
                )}
                {s.improve && <span className="report-improve-link">{openSubject === s.id ? `${t('Hide')} ↑` : `${t('How to improve')} →`}</span>}
              </li>
            ))}
            {extraSubjects}
          </ul>



          {card.assignments.length > 0 && (
            <div className="report-assignments">
              <h3>{w('Then, next week', 'Then, next month')}</h3>
              <ol>
                {card.assignments.map((a) => <li key={a.id}><strong>{a.label}:</strong> {a.tip}</li>)}
              </ol>
            </div>
          )}

          {renderFooter && renderFooter(card, periodText)}

          <details className="report-how">
            <summary>{t('How these grades work')}</summary>
            <p>{t('Each grade comes only from your trades{journal} for this week. The overall grade is weighted, and how you traded counts more than what you made: it can’t be more than a few points above your grades without Results, so a green week traded badly still gets a low grade.',
              { journal: noJournal ? '' : t(' and journal') })}</p>
            <p>{t('A monthly grade uses the same rules over the whole month. Counts like losses far bigger than usual or trades after 2 losses are averaged per week traded, so a month isn’t graded four times as hard as a week.')}</p>
            <ul>
              {card.subjects.map((s) => <li key={s.id}><strong>{s.label} ({s.weight}%):</strong> {s.measures}</li>)}
            </ul>
            <p>{t('A grade marked “Early signal” rests on only a few trades or days, so treat it as something to watch, not a verdict. A week needs at least {min} trades to be graded at all, and a month at least {month}.', { min: MIN_WEEK_TRADES, month: MIN_MONTH_TRADES })} {t('A quarter needs at least {n}, and is graded like a month, with its counts averaged per week traded.', { n: MIN_QUARTER_TRADES })}</p>
          </details>
        </>
      )}
    </section>
  );
}
